import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Divider,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import VoterRegistration from "./../../assets/images/icons/_x31_9_Voter_Registration.png";
import NoVoterRegistration from "./../../assets/icons/not-eligible-check-icon.svg";
import FileText from "./../../assets/images/icons/file-text.png";
import FileCheck from "./../../assets/images/icons/file-check.png";
import CreditCard from "./../../assets/images/icons/credit-card.png";
import ListDetails from "./../../assets/images/icons/list-details.png";
import EligibilityCard, { StepData } from "../../components/ui/EligibilityCard";
import { PrimaryButton } from "../../components/ui/Button";
import { AppDispatch, RootState } from "../../services/redux/store";
import axiosInstance from "../../services/api/interceptor/interceptor";
import { decrypt, encrypt } from "../../utils/crypto";
import {
  authStatusSelector,
  setUserAuth,
  setUserLoanCategory,
  setUserBusinessType,
  setUserPermission,
  setUserProfileData,
} from "../../services/redux/slices/authSlice";
import {
  setActiveHorizontalIndex,
  setIsBypassLogin,
  setIsEligibilityCheckSubmitted,
  setIsEligible,
} from "../../services/redux/slices/stepperDynamicFormSlice";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import { setBreData } from "../../services/redux/slices/stepperDynamicFormSlice";

// Props
interface Props {
  onNext: () => void;
  firstName: string;
  provisionalOffer: any;
}

// ✅ Step data for EligibilityCard
const steps: StepData[] = [
  {
    icon: ListDetails,
    title: "Applicant & Loan Details",
  },
  {
    icon: FileText,
    title: "Provisional Offer Letter",
  },
  {
    icon: FileText,
    title: "Additional Details & Documents",
  },
  {
    icon: FileCheck,
    title: "Final Verification & Sanction",
  },
  {
    icon: CreditCard,
    title: "Disbursement of Funds",
  },
];

// ✅ Main Component
const EligibilityConfirmation: React.FC<Props> = ({
  onNext,
  firstName,
  provisionalOffer,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mobile = useSelector(
    (state: RootState) => state?.stepper?.formData?.phone_number
  );
  const [isContinueDisabled, setIsContinueDisabled] = useState(false);
  const isAuthenticated = useSelector(authStatusSelector);
  const trackingId = useSelector(
    (state: RootState) => state?.stepper?.trackingId
  );
  const { isEligible } = useSelector((state: RootState) => state?.stepper);
  const breStatus = useSelector(
    (state: RootState) => state?.stepper?.breStatus
  );
  const breData = useSelector((state: RootState) => state.stepper.breData);

  const handleBreInvokApi = async () => {
    try {
      const payload = {
        applicationId: trackingId,
        name: "getCustomerEligibility",
      };
      const res: any = await eligibilityCheckServices.fetchBreApi(payload);
      if (res?.data?.data?.Status != "success") {
        setIsContinueDisabled(true);
        dispatch(setIsEligible(false));
        return;
      }
      dispatch(setBreData(res?.data?.data));
      dispatch(setIsEligible(res?.data?.data?.Scheme_Available == "Yes"));
    } catch (e) {
      dispatch(
        setStatusPopup({
          status: "info",
          message: "Something went wrong !",
        })
      );
      setIsContinueDisabled(true);
      return;
    }
  };

  useEffect(() => {
    if (isAuthenticated && breStatus == "FROM_APPLICATION") {
      handleBreInvokApi();
    }
    if (isAuthenticated && breStatus == "FROM_FORM") {
      dispatch(setBreData(provisionalOffer));
    }
  }, [trackingId, isAuthenticated, breStatus]);

  const handleContinueClick = async () => {
    if (isAuthenticated) {
      dispatch(setActiveHorizontalIndex(1));
      // dispatch(setIsEligibilityCheckSubmitted(true));
      return;
    } else {
      try {
        const encryptedKey = encrypt(mobile);
        const resp: any = await axiosInstance.post(
          "api/v1/user-lead/bypass-login",
          {
            key: encryptedKey,
          }
        );
        if (resp.data?.status === "success") {
          const data: any = JSON.parse(decrypt(resp?.data?.data));

          dispatch(setUserProfileData(data?.user));
          dispatch(setUserPermission(data?.permission));
          dispatch(
            setUserBusinessType(
              data?.businessType.length > 0 ? data?.businessType?.[0] : {}
            )
          );
          dispatch(setUserLoanCategory(data?.result));
          dispatch(setUserAuth(data?.token));
          dispatch(setIsBypassLogin(true));
          dispatch(setIsEligibilityCheckSubmitted(true));
        } else {
          console.error(resp.data?.message || "Bypass login failed");
        }
      } catch (error: any) {
        console.error(error?.response?.data?.message || "Bypass login failed");
      }
    }
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      className="eligibility-container"
      sx={{
        px: { xs: 0, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
      }}
    >
      {/* ✅ Header Icon */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        mb={{ xs: 2, sm: 3 }}
      >
        <Box
          my={{ xs: 1, sm: 2 }}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src={isEligible ? VoterRegistration : NoVoterRegistration}
            alt="Voter Registration"
            width={isMobile ? 80 : 117}
            height={isMobile ? 64 : 94}
            loading="lazy"
            style={{
              maxWidth: "100%",
              height: "auto",
            }}
          />
        </Box>

        {/* ✅ Heading */}
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: "#008688",
            fontSize: { xs: "20px", sm: "22px", md: "24px" },
            lineHeight: "120%",
            letterSpacing: "-0.5px",
            textAlign: "center",
            px: { xs: 1, sm: 0 },
            mt: { xs: 1, sm: 2 },
          }}
        >
          {isEligible && <>Congratulations! </>}

          <Box component="span" fontWeight={700} color="#000">
            {isEligible ? "You're eligible," : "You’re not eligible"}{" "}
            {firstName}!!
          </Box>
        </Typography>

        {/* ✅ Paragraph Text */}
        <Typography
          variant="body2"
          className="eligibility-message-text"
          sx={{
            mb: { xs: 1, sm: 1 },
            color: "text.secondary",
            maxWidth: 600,
            fontSize: { xs: "14px", sm: "16px" },
            lineHeight: { xs: "1.4", sm: "1.5" },
            px: { xs: 1, sm: 0 },
          }}
        >
          {isEligible ? (
            <>
              We're excited to let you know that based on the details provided,
              we have a Student Loan Product perfectly suited for your
              international education plans!
            </>
          ) : (
            <>
              Unfortunately, based on the details you provided, we don't have a
              Student Loan Product that's a good fit for your international
              education plans right now.
            </>
          )}
        </Typography>

        <Typography
          variant="body2"
          className="eligibility-message-text"
          sx={{
            color: "text.secondary",
            maxWidth: 600,
            fontSize: { xs: "14px", sm: "16px" },
            lineHeight: { xs: "1.4", sm: "1.5" },
            px: { xs: 1, sm: 0 },
          }}
        >
          {isEligible ? (
            <>
              This means we can help you finance your academic journey abroad
              and take a significant step towards achieving your goals.
            </>
          ) : (
            <>
              However, we're always working to expand our offerings. We will be
              sure to contact you if a product becomes available that aligns
              with your requirements.
            </>
          )}
        </Typography>
      </Box>

      {isEligible && (
        <>
          {/* ✅ Divider */}
          <Divider sx={{ my: { xs: 2, sm: 3 } }} />

          {/* ✅ Section Header */}
          <Typography
            className="eligibility-sub-heading"
            mt={{ xs: 1, sm: 2 }}
            mb={{ xs: 1.5, sm: 2 }}
            fontWeight={600}
            fontSize={{ xs: "16px", sm: "18px" }}
            color="#333"
            sx={{
              px: { xs: 1, sm: 0 },
            }}
          >
            Next Steps
          </Typography>

          {/* ✅ Steps using EligibilityCard */}
          <Box
            sx={{
              mb: { xs: 2, sm: 3 },
              px: { xs: 0, sm: 0 },
              width: "100%",
            }}
          >
            <EligibilityCard steps={steps} />
          </Box>

          {/* ✅ CTA Button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", sm: "flex-start" },
              mt: { xs: 2, sm: 3 },
            }}
          >
            <Grid
              container
              spacing={2}
              justifyContent={{ xs: "center", sm: "flex-start" }}
            >
              <Grid item xs={12} sm={6} md={4}>
                <PrimaryButton
                  type="submit"
                  fullWidth
                  onClick={handleContinueClick}
                  disabled={isContinueDisabled}
                  sx={{
                    py: { xs: 1.5, sm: 2 },
                    fontSize: { xs: "14px", sm: "16px" },
                    minHeight: { xs: "48px", sm: "56px" },
                  }}
                >
                  Continue with Application
                </PrimaryButton>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Container>
  );
};

export default EligibilityConfirmation;
